import type { Locale } from './config.js';
import { DEFAULT_NAMESPACE } from './config.js';

import enCommon from './locales/en/common.json';
import enNav from './locales/en/nav.json';
import zhCommon from './locales/zh/common.json';
import zhNav from './locales/zh/nav.json';
import msCommon from './locales/ms/common.json';
import msNav from './locales/ms/nav.json';

/**
 * All three catalogs are imported statically and bundled. They are small (a few KB each), so
 * lazy-loading them over HTTP would cost a round trip and a loading state for no real benefit.
 * If the catalogs ever grow past ~50 KB per locale, swap this for `i18next-http-backend` and move
 * the JSON into apps/web/public/locales/.
 */

/**
 * English is the reference catalog — every other locale must match its shape exactly.
 *
 * Deliberately a type alias, not an interface: i18next's `Resource` is indexed by string, and
 * only type aliases get the implicit index signature that makes them assignable to it.
 */
export type Catalog = {
  common: typeof enCommon;
  nav: typeof enNav;
};

/**
 * `Record<Locale, Catalog>` is what makes translation coverage a compile-time property: a key
 * added to en/ but forgotten in zh/ or ms/ fails `pnpm typecheck` instead of silently falling
 * back to English at runtime.
 */
export const resources: Record<Locale, Catalog> = {
  en: { common: enCommon, nav: enNav },
  zh: { common: zhCommon, nav: zhNav },
  ms: { common: msCommon, nav: msNav },
};

/**
 * Teaches i18next what our keys are, so `t('home.title')` is autocompleted and `t('home.titel')`
 * is a type error. This augmentation only applies where the file is part of the program — which
 * it always is, because i18n.ts imports `resources` from here.
 */
declare module 'i18next' {
  interface CustomTypeOptions {
    defaultNS: typeof DEFAULT_NAMESPACE;
    resources: Catalog;
  }
}
